import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex, Form, Input, Typography, Select, Space, Divider, Checkbox, CheckboxChangeEvent, Col, Row } from 'antd';
import { ValidatorRule } from 'rc-field-form/lib/interface';
import { StyledFormSubTitle, StyledIssueDetails, StyledIssueDetailsFormItem } from './IssueDetails.styles';
import { IssueDetailsFormValues } from './IssueDetails.types';
import { KccButton } from 'lib/components/KccButton/KccButton';
import { SvgIcon } from 'lib/components/SvgIcon/SvgIcon';
import { AddressAutocomplete } from 'app/components/AddressAutocomplete';
import { countries } from 'app/modules/notification/NotificationsSubscriptionsDrawer/UpsertNotificationsOperator/countryData/countryData';
import { kccThemeConstants } from 'config/theme/kccTheme/kccTheme';
import { useServiceRequestContext } from 'app/modules/service/context/ServiceContext';
import { GERMAN_COUNTRY_CODE, PHONE_NUMBER_REGEX } from 'app/modules/service/utils/constants';

const SELF_REQUIRED_FIELDS = [
  'company',
  'address',
  'city',
  'state',
  'zipCode',
  'firstName',
  'lastName',
  'email',
  'countryCode',
  'phoneNumber',
];

export const IssueDetails = (): JSX.Element => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const { handleAddServiceCaseNext, handleAddServiceCaseBack, handleIssueDetails, issueDetailsData, emailRequested } =
    useServiceRequestContext();

  const [isContactPersonChecked, setIsContactPersonChecked] = useState(
    issueDetailsData?.contactPerson !== undefined ? issueDetailsData.contactPerson : true
  );
  const [isReviewDisabled, setIsReviewDisabled] = useState(true);
  const [countryCode, setCountryCode] = useState(issueDetailsData?.countryCode || GERMAN_COUNTRY_CODE);
  const [contactCountryCode, setContactCountryCode] = useState(
    issueDetailsData?.contactCountryCode || GERMAN_COUNTRY_CODE
  );
  const [phoneNumberError, setPhoneNumberError] = useState(true);
  const [contactPhoneNumberError, setContactPhoneNumberError] = useState(true);

  const handleContactPersonChange = (event: CheckboxChangeEvent): void => {
    setIsContactPersonChecked(event.target.checked);
  };

  const handleFieldsChange = (): void => {
    const hasErrors = form.getFieldsError(SELF_REQUIRED_FIELDS).some(field => field.errors.length > 0);

    const values = form.getFieldsValue(SELF_REQUIRED_FIELDS);
    const selfRequiredFieldsFilled = SELF_REQUIRED_FIELDS.every(fieldName => {
      const value = values[fieldName];
      return value !== undefined && value !== null && value !== '';
    });

    setIsReviewDisabled(hasErrors || !selfRequiredFieldsFilled);
  };

  const handleAddressSelect = (components: { address: string; city: string; state: string; zipCode: string }): void => {
    form.setFieldsValue({
      address: components.address,
      city: components.city,
      state: components.state,
      zipCode: components.zipCode,
    });
  };

  const handleCountryCodeChange = (value: string): void => {
    setCountryCode(value);
  };

  const handleContactCountryCodeChange = (value: string): void => {
    setContactCountryCode(value);
  };

  const onValuesChange = (_: IssueDetailsFormValues, allFields: IssueDetailsFormValues): void => {
    handleIssueDetails({ ...allFields, countryCode, contactCountryCode });
  };

  const onReview = (): void => {
    form.submit();
  };

  const onFinish = (): void => {
    handleAddServiceCaseNext();
  };

  useEffect(() => {
    const values = form.getFieldsValue(SELF_REQUIRED_FIELDS);

    const selfRequiredFieldsFilled = SELF_REQUIRED_FIELDS.every(
      fieldName => values[fieldName] !== undefined && values[fieldName] !== null && values[fieldName] !== ''
    );

    setIsReviewDisabled(!selfRequiredFieldsFilled);
  }, [form]);

  useEffect(() => {
    if (issueDetailsData.phoneNumber) {
      setPhoneNumberError(false);
    }

    if (issueDetailsData.contactPhoneNumber) {
      setContactPhoneNumberError(false);
    }
  }, [issueDetailsData.phoneNumber, issueDetailsData.contactPhoneNumber]);

  return (
    <StyledIssueDetails vertical gap={24}>
      <Typography.Title level={3} style={{ margin: 0 }}>
        {t('service.issueDetails.title')}
      </Typography.Title>

      <Form
        form={form}
        layout="vertical"
        autoComplete="off"
        onFieldsChange={handleFieldsChange}
        requiredMark="optional"
        style={{ width: '100%', margin: 0, maxWidth: 755 }}
        initialValues={{
          email: emailRequested,
          countryCode: GERMAN_COUNTRY_CODE,
          contactCountryCode: GERMAN_COUNTRY_CODE,
          contactPerson: isContactPersonChecked,
          ...issueDetailsData,
        }}
        onFinish={onFinish}
        onValuesChange={onValuesChange}
      >
        <Flex vertical>
          <StyledFormSubTitle level={5}>{t('service.issueDetails.section.companyDetails')}</StyledFormSubTitle>

          <StyledIssueDetailsFormItem
            extra={<span className="issue-details__extra">{t('service.issueDetails.form.company.extra')}</span>}
            name="company"
            label={t('service.issueDetails.form.company.label')}
            required
          >
            <Input />
          </StyledIssueDetailsFormItem>
        </Flex>

        <Divider style={{ marginTop: 0 }} />

        <Flex vertical>
          <StyledFormSubTitle level={5}>{t('service.issueDetails.section.addressOfDevice')}</StyledFormSubTitle>

          <StyledIssueDetailsFormItem
            extra={<span className="issue-details__extra">{t('service.issueDetails.form.address.extra')}</span>}
            name="address"
            label={t('service.issueDetails.form.address.label')}
            required
          >
            <AddressAutocomplete onAddressSelect={handleAddressSelect} />
          </StyledIssueDetailsFormItem>

          <StyledIssueDetailsFormItem name="factoryNumber" label={t('service.issueDetails.form.factoryNumber.label')}>
            <Input placeholder={t('service.issueDetails.form.factoryNumber.placeholder')} />
          </StyledIssueDetailsFormItem>

          <StyledIssueDetailsFormItem name="city" label={t('service.issueDetails.form.city.label')} required>
            <Input />
          </StyledIssueDetailsFormItem>

          <Row gutter={24}>
            <Col xs={24} md={12}>
              <StyledIssueDetailsFormItem name="state" label={t('service.issueDetails.form.state.label')} required>
                <Input />
              </StyledIssueDetailsFormItem>
            </Col>
            <Col xs={24} md={12}>
              <StyledIssueDetailsFormItem name="zipCode" label={t('service.issueDetails.form.zipCode.label')} required>
                <Input />
              </StyledIssueDetailsFormItem>
            </Col>
          </Row>
        </Flex>

        <Divider style={{ marginTop: 0 }} />

        <Flex vertical>
          <StyledFormSubTitle level={5}>{t('service.issueDetails.section.yourDetails')}</StyledFormSubTitle>

          <StyledIssueDetailsFormItem name="firstName" label={t('service.issueDetails.form.firstName.label')} required>
            <Input />
          </StyledIssueDetailsFormItem>

          <StyledIssueDetailsFormItem name="lastName" label={t('service.issueDetails.form.lastName.label')} required>
            <Input />
          </StyledIssueDetailsFormItem>

          <StyledIssueDetailsFormItem
            name="email"
            label={t('service.issueDetails.form.email.label')}
            className="issue-details__form-item-border-less"
            required
          >
            <Input variant="borderless" disabled style={{ padding: 0, color: 'black', margin: 0 }} />
          </StyledIssueDetailsFormItem>

          <StyledIssueDetailsFormItem
            name="phoneNumber"
            label={t('service.issueDetails.form.phoneNumber.label')}
            required
            rules={[
              {
                pattern: PHONE_NUMBER_REGEX,
                message: (
                  <Flex align="center" gap={6}>
                    <SvgIcon name="warningCritical" />
                    {t('service.issueDetails.form.errors.phoneNumberInvalid')}
                  </Flex>
                ),
              },
              (): ValidatorRule => ({
                validator(_: unknown, value: string): Promise<void> {
                  if (value && PHONE_NUMBER_REGEX.test(value)) {
                    setPhoneNumberError(false);
                    return Promise.resolve();
                  }

                  setPhoneNumberError(true);
                  return Promise.reject();
                },
              }),
            ]}
          >
            <Space.Compact style={{ width: '100%' }}>
              <Select
                className={!phoneNumberError ? 'country-code-validated' : ''}
                style={{ width: '110px', height: '40px' }}
                defaultValue={countryCode}
                options={countries.map(country => ({
                  label: country.phoneCode,
                  value: country.phoneCode,
                }))}
                onChange={handleCountryCodeChange}
                showSearch
              />

              <Input
                inputMode="numeric"
                defaultValue={issueDetailsData?.phoneNumber}
                style={{ width: 'auto', height: '40px', flex: 1 }}
                placeholder={t('service.issueDetails.form.phoneNumber.placeholder')}
              />
            </Space.Compact>
          </StyledIssueDetailsFormItem>
        </Flex>

        <StyledIssueDetailsFormItem
          name="contactPerson"
          valuePropName="checked"
          label={null}
          className="issue-details__form-item-contact-person"
        >
          <Checkbox checked={isContactPersonChecked} onChange={handleContactPersonChange}>
            {t('service.issueDetails.form.contactPerson.label')}
          </Checkbox>
        </StyledIssueDetailsFormItem>

        {!isContactPersonChecked && (
          <Flex
            vertical
            style={{
              padding: kccThemeConstants.PADDING.CONTENT_HORIZONTAL_SM,
              border: `2px solid ${kccThemeConstants.COLORS.BORDER}`,
            }}
          >
            <Typography.Text
              style={{
                marginBottom: kccThemeConstants.SPACING.LG,
                fontWeight: kccThemeConstants.TYPOGRAPHY.FONT_WEIGHT.EXTRA_BOLD,
              }}
            >
              {t('service.issueDetails.form.contactPerson.title')}
            </Typography.Text>

            <StyledIssueDetailsFormItem
              name="contactFirstName"
              label={t('service.issueDetails.form.firstName.label')}
              required
              rules={[
                {
                  required: true,
                  message: (
                    <Flex align="center" gap={6}>
                      <SvgIcon name="warningCritical" />
                      {t('service.issueDetails.form.errors.firstNameRequired')}
                    </Flex>
                  ),
                },
              ]}
            >
              <Input />
            </StyledIssueDetailsFormItem>

            <StyledIssueDetailsFormItem
              name="contactLastName"
              label={t('service.issueDetails.form.lastName.label')}
              required
              rules={[
                {
                  required: true,
                  message: (
                    <Flex align="center" gap={6}>
                      <SvgIcon name="warningCritical" />
                      {t('service.issueDetails.form.errors.lastNameRequired')}
                    </Flex>
                  ),
                },
              ]}
            >
              <Input />
            </StyledIssueDetailsFormItem>

            <StyledIssueDetailsFormItem
              name="contactEmail"
              label={t('service.issueDetails.form.email.label')}
              required
              rules={[
                {
                  required: true,
                  message: (
                    <Flex align="center" gap={6}>
                      <SvgIcon name="warningCritical" />
                      {t('service.issueDetails.form.errors.emailRequired')}
                    </Flex>
                  ),
                },
                {
                  type: 'email',
                  message: (
                    <Flex align="center" gap={6}>
                      <SvgIcon name="warningCritical" />
                      {t('service.issueDetails.form.errors.emailRequired')}
                    </Flex>
                  ),
                },
              ]}
            >
              <Input type="email" />
            </StyledIssueDetailsFormItem>

            <StyledIssueDetailsFormItem
              name="contactPhoneNumber"
              label={t('service.issueDetails.form.phoneNumber.label')}
              required
              rules={[
                {
                  required: true,
                  message: (
                    <Flex align="center" gap={6}>
                      <SvgIcon name="warningCritical" />
                      {t('service.issueDetails.form.errors.phoneNumberRequired')}
                    </Flex>
                  ),
                },
                {
                  pattern: PHONE_NUMBER_REGEX,
                  message: (
                    <Flex align="center" gap={6}>
                      <SvgIcon name="warningCritical" />
                      {t('service.issueDetails.form.errors.phoneNumberRequired')}
                    </Flex>
                  ),
                },
                (): ValidatorRule => ({
                  validator(_: unknown, value: string): Promise<void> {
                    if (value && PHONE_NUMBER_REGEX.test(value)) {
                      setContactPhoneNumberError(false);
                      return Promise.resolve();
                    }

                    setContactPhoneNumberError(true);
                    return Promise.reject();
                  },
                }),
              ]}
            >
              <Space.Compact style={{ width: '100%' }}>
                <Select
                  className={!contactPhoneNumberError ? 'country-code-validated' : ''}
                  style={{ width: '110px', height: '40px' }}
                  options={countries.map(country => ({
                    label: country.phoneCode,
                    value: country.phoneCode,
                  }))}
                  onChange={handleContactCountryCodeChange}
                  defaultValue={contactCountryCode}
                  showSearch
                />

                <Input
                  defaultValue={issueDetailsData?.contactPhoneNumber}
                  inputMode="numeric"
                  style={{ width: 'auto', height: '40px', flex: 1 }}
                  placeholder={t('service.issueDetails.form.phoneNumber.placeholder')}
                />
              </Space.Compact>
            </StyledIssueDetailsFormItem>
          </Flex>
        )}

        <Divider />

        <Flex vertical>
          <StyledFormSubTitle level={5}>{t('service.issueDetails.section.purchaseOrderDetails')}</StyledFormSubTitle>

          <StyledIssueDetailsFormItem
            name="purchaseOrderNumber"
            label={t('service.issueDetails.form.purchaseOrderNumber.label')}
            style={{ marginBottom: 0 }}
          >
            <Input placeholder={t('service.issueDetails.form.purchaseOrderNumber.placeholder')} />
          </StyledIssueDetailsFormItem>
        </Flex>
      </Form>

      <Flex
        justify="space-between"
        align="center"
        style={{ width: '100%', maxWidth: kccThemeConstants.SCREEN.CONTENT_MAX_WIDTH }}
      >
        <KccButton
          type="link"
          onClick={handleAddServiceCaseBack}
          icon={<SvgIcon name="leftArrow" className="back-icon" />}
          iconPosition="start"
          className="issue-details__back-btn"
        >
          {t('common.back')}
        </KccButton>

        <KccButton
          disabled={isReviewDisabled}
          icon={<SvgIcon name="rightArrow" className="next-icon" />}
          iconPosition="end"
          className="issue-details__review-btn"
          onClick={onReview}
          type="secondary"
        >
          {t('common.review')}
        </KccButton>
      </Flex>
    </StyledIssueDetails>
  );
};
